import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { cn } from "@base/lib/utils";
import { useOverlay } from "@base/model/hooks";
import { AppLink } from "@base/ui/AppLink";
import { useSafeAreaInset } from "@base/ui/SafeArea/useSafeAreaInset";
import { Button, DatePicker } from "@buildblockinc/buildblock-design-system";
import { useProjectDetailQuery } from "@features/project-detail/model/useProjectDetailQuery";
import type { WorkHistoryDailyStat } from "@features/project-detail/model/useWorkHistorySummaryQuery";
import { useWorkHistorySummaryQuery } from "@features/project-detail/model/useWorkHistorySummaryQuery";
import { useIsWorkHistoryDesktopPanel } from "@features/project-detail/model/workHistoryLayout";
import { resolveSelectedWorkHistoryDate } from "@features/project-detail/model/workHistorySelection";
import { computeWorkHistoryStatSummary } from "@features/project-detail/model/workHistoryStats";
import dayjs from "dayjs";
import { AlertCircle, ArrowUpRight, CalendarCheck, HardHat, Pickaxe, SquareArrowOutUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { ExportWorkHistoryDialog } from "./ExportWorkHistoryDialog";
import { WorkHistoryBottomSheet } from "./WorkHistoryBottomSheet";
import type { WorkHistoryTabKey } from "./WorkHistoryDayContent";
import { WorkHistoryDayContent } from "./WorkHistoryDayContent";

function DailyWorkListSkeleton({ isDesktopPanel }: { isDesktopPanel: boolean }) {
  return (
    <div className="flex overflow-hidden flex-col flex-1 gap-4 animate-pulse">
      <div className="flex gap-2 items-center md:gap-3 md:items-stretch">
        <div className="flex flex-col flex-1 gap-2 justify-center items-center p-4 border border-gray-200 rounded-8 md:items-start md:justify-start">
          <div className="w-4 h-4 rounded-2 skeleton-shimmer" />
          <div className="w-20 h-3 rounded-2 skeleton-shimmer" />
          <div className="w-10 h-5 rounded-2 skeleton-shimmer" />
          <div className="hidden w-24 h-3 rounded-2 md:block skeleton-shimmer" />
        </div>
        <div className="flex flex-col flex-1 gap-2 justify-center items-center p-4 border border-gray-200 rounded-8 md:items-start md:justify-start">
          <div className="w-4 h-4 rounded-2 skeleton-shimmer" />
          <div className="w-20 h-3 rounded-2 skeleton-shimmer" />
          <div className="w-10 h-5 rounded-2 skeleton-shimmer" />
          <div className="hidden w-24 h-3 rounded-2 md:block skeleton-shimmer" />
        </div>
        <div className="hidden flex-col flex-1 gap-2 items-start p-4 border border-gray-200 rounded-8 md:flex">
          <div className="w-4 h-4 rounded-2 skeleton-shimmer" />
          <div className="w-24 h-3 rounded-2 skeleton-shimmer" />
          <div className="w-14 h-5 rounded-2 skeleton-shimmer" />
          <div className="w-20 h-3 rounded-2 skeleton-shimmer" />
        </div>
      </div>

      <div className={cn("flex overflow-hidden flex-1 gap-4", isDesktopPanel ? "flex-row" : "flex-col")}>
        <div className="flex overflow-hidden flex-col flex-1 gap-2">
          <div className="flex justify-between items-center">
            <div className="w-28 h-4 rounded-4 skeleton-shimmer" />
            <div className="w-8 h-8 rounded-4 skeleton-shimmer" />
          </div>
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }, (_, i) => `skeleton-item-${i}`).map((key) => (
              <div key={key} className="flex gap-4 items-center p-2 bg-white shadow-box rounded-8">
                <div className="w-10 h-10 rounded-4 skeleton-shimmer shrink-0" />
                <div className="flex flex-col flex-1 gap-2">
                  <div className="w-32 h-3 rounded-4 skeleton-shimmer" />
                  <div className="w-28 h-3 rounded-4 skeleton-shimmer" />
                </div>
              </div>
            ))}
          </div>
        </div>
        {isDesktopPanel && (
          <div className="w-[360px] shrink-0 rounded-16 border border-gray-200 bg-white p-4">
            <div className="w-40 h-4 rounded-4 skeleton-shimmer" />
          </div>
        )}
      </div>
    </div>
  );
}

function WorkHistoryBreakdownHeader({ onExport }: { onExport: () => void }) {
  return (
    <div className="flex justify-between items-center">
      <h2 className="text-subtitle4 text-gray-1000">Daily Breakdown</h2>
      <button type="button" className="p-2 text-blue-500 rounded-4 bg-blue-10" onClick={onExport}>
        <SquareArrowOutUpRight width={16} height={16} />
      </button>
    </div>
  );
}

function EmptyRangeState() {
  return (
    <div className="flex flex-col flex-1 gap-2 justify-center items-center py-8 text-gray-400">
      <p className="text-body4">No daily logs recorded in this date range.</p>
    </div>
  );
}

function EmptyPanelState() {
  return (
    <div className="flex flex-col flex-1 gap-2 justify-center items-center py-8 text-center text-gray-400">
      <p className="text-body4">Select a date with a daily log to see its details.</p>
    </div>
  );
}

interface WorkHistoryRowProps {
  stat: WorkHistoryDailyStat;
  projectId: string;
  isSelected: boolean;
  onSelect: () => void;
}

function WorkHistoryTableHead() {
  return (
    <thead className="sticky top-0 z-10 bg-white">
      <tr className="border-b border-gray-200 text-label3 font-semibold text-gray-600">
        <th scope="col" className="px-4 py-3 text-left">
          Date
        </th>
        <th scope="col" className="px-4 py-3 w-20 text-left">
          Day
        </th>
        <th scope="col" className="px-4 py-3 w-28 text-left">
          Workforce
        </th>
        <th scope="col" className="px-4 py-3 w-28 text-left">
          Equipment
        </th>
        <th scope="col" className="px-4 py-3 w-12 text-left" aria-hidden="true" />
      </tr>
    </thead>
  );
}

/** ≥1280px: 좌측 표의 한 행. 일지별 행이므로 같은 날짜에 여러 행이 있을 수 있다. */
function WorkHistoryTableRow({ stat, projectId, isSelected, onSelect }: WorkHistoryRowProps) {
  const hasLog = stat.daily_log_id !== null;
  const formattedDate = dayjs(`${stat.date}T00:00:00`).format("MMM D, YYYY");
  return (
    <tr
      tabIndex={0}
      aria-selected={isSelected}
      onClick={onSelect}
      onKeyDown={(event) => {
        // 행 안의 상세 링크에서 올라온 Enter는 링크의 기본 이동에 맡긴다.
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        // 배경은 흰색으로 통일하고 hover에 색 변화를 주지 않는다. 선택된 행만 왼쪽 막대와 옅은 파랑으로 구분한다.
        // 키보드 이동 시에만 보이는 테두리(focus-visible)는 접근성을 위해 유지한다.
        "border-t border-gray-100 cursor-pointer bg-white",
        "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-500",
        isSelected && "bg-blue-10",
      )}
    >
      {/* 모든 행에 같은 두께의 왼쪽 테두리를 두고 선택된 행만 칠해, 선택이 바뀌어도 글자가 밀리지 않게 한다 */}
      <td
        className={cn(
          "px-4 py-3 text-label2 border-l-[3px]",
          hasLog ? "font-semibold" : "text-gray-500",
          isSelected ? "border-l-blue-500 text-blue-600" : "border-l-transparent",
          !isSelected && hasLog && "text-gray-1000",
        )}
      >
        {formattedDate}
      </td>
      <td className="px-4 py-3 text-label2 text-gray-700">{stat.day_of_week}</td>
      <td className={cn("px-4 py-3 text-label2", hasLog ? "text-gray-900" : "text-gray-500")}>
        {hasLog ? stat.workforce_quantity : "No log"}
      </td>
      <td className={cn("px-4 py-3 text-label2", hasLog ? "text-gray-900" : "text-gray-500")}>
        {hasLog ? stat.equipment_quantity : "No log"}
      </td>
      <td className="px-4 py-3">
        {hasLog && (
          <AppLink
            to="/projects/$projectId/log/$logId"
            params={{ projectId, logId: String(stat.daily_log_id) }}
            aria-label={`Open daily log for ${formattedDate}`}
            className="inline-flex p-1 text-gray-500 rounded-2"
            onClick={(event) => event.stopPropagation()}
          >
            <ArrowUpRight width={16} height={16} />
          </AppLink>
        )}
      </td>
    </tr>
  );
}

/** <1280px: 카드 목록의 한 행. */
function WorkHistoryListItem({ stat, projectId, onSelect }: Omit<WorkHistoryRowProps, "isSelected">) {
  const hasLog = stat.daily_log_id !== null;
  return (
    <li className="shadow-box rounded-8">
      <div className="flex gap-2 items-center p-2 w-full bg-white rounded-8">
        <button type="button" className="flex flex-1 gap-4 items-center min-w-0 text-left" onClick={onSelect}>
          <span className="flex flex-col justify-center items-center w-10 h-10 text-blue-500 bg-blue-10 rounded-4 shrink-0">
            <span className="text-label3">{dayjs(`${stat.date}T00:00:00`).format("MMM")}</span>
            <span className="text-subtitle6">{stat.date.split("-").slice(-1)[0]}</span>
          </span>

          <span className="flex flex-col flex-1 gap-2 min-w-0">
            <span className="flex items-center gap-[6px] text-label2">
              <HardHat width={16} height={16} />
              Workforce - {hasLog ? stat.workforce_quantity : "No log"}
            </span>

            <span className="flex items-center gap-[6px] text-label2">
              <Pickaxe width={16} height={16} />
              Equipment - {hasLog ? stat.equipment_quantity : "No log"}
            </span>
          </span>
        </button>

        {hasLog && (
          <AppLink
            to="/projects/$projectId/log/$logId"
            params={{ projectId, logId: String(stat.daily_log_id) }}
            aria-label={`Open daily log for ${dayjs(`${stat.date}T00:00:00`).format("MMM D, YYYY")}`}
            className="p-2 text-gray-500 rounded-4 shrink-0 hover:text-blue-500"
          >
            <ArrowUpRight width={16} height={16} />
          </AppLink>
        )}
      </div>
    </li>
  );
}

function dailyStatKey(stat: WorkHistoryDailyStat, idx: number) {
  return stat.daily_log_id !== null ? `log-${stat.daily_log_id}` : `${stat.date}-${stat.day_of_week}-${idx}`;
}

const DailyWorkList = ({ projectId }: { projectId: string }) => {
  const [[startDate, endDate], setDateRange] = useState<[string | undefined, string | undefined]>([
    undefined,
    undefined,
  ]);
  /** 사용자가 직접 고른 날짜. 표시할 날짜는 아래에서 화면 폭에 맞춰 정한다 */
  const [userSelectedDate, setUserSelectedDate] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<WorkHistoryTabKey>("workforce");

  const { bottom } = useSafeAreaInset();
  const isDesktopPanel = useIsWorkHistoryDesktopPanel();

  const { data, isLoading, isError, refetch } = useWorkHistorySummaryQuery(projectId, startDate, endDate);

  const { open: openExportWorkHistoryDialog } = useOverlay();

  const { data: project } = useProjectDetailQuery(projectId);

  // 기간을 다시 선택하지 않은(=비운) 상태로 돌아가면 선택 날짜도 비운다.
  // startDate·endDate는 같은 setDateRange 호출로 항상 함께 갱신되는 하나의 튜플이다.
  useEffect(() => {
    if (!startDate || !endDate) {
      setUserSelectedDate(null);
    }
  }, [startDate]);

  const handleSelectDate = (date: string) => {
    EventLogger.view(CLIENT_EVENT.WORK_HISTORY_DAY_VIEW, { project_id: projectId, date });
    setUserSelectedDate(date);
  };

  const openExportDialog = () =>
    openExportWorkHistoryDialog(({ isOpen, close }) => (
      <ExportWorkHistoryDialog
        open={isOpen}
        onClose={close}
        projectId={projectId}
        startDate={startDate ?? ""}
        endDate={endDate ?? ""}
      />
    ));

  const dailyStats = data?.daily_stats ?? [];
  const statSummary = computeWorkHistoryStatSummary(
    dailyStats,
    data?.summary.total_workforce ?? 0,
    data?.summary.total_equipment ?? 0,
  );
  const selectedDate = resolveSelectedWorkHistoryDate(userSelectedDate, dailyStats, {
    autoSelectLatest: isDesktopPanel,
  });
  const selectedDailyLogId = dailyStats.find((stat) => stat.date === selectedDate)?.daily_log_id ?? null;

  return (
    <div className="flex overflow-hidden flex-col flex-1 gap-4">
      <div className="md:w-[360px]">
        <DatePicker
          selectionMode="range"
          color="secondary"
          onValueChange={({ valueAsString: [startDate, endDate] }) => {
            setDateRange([startDate, endDate]);
          }}
          isDateUnavailable={(date) => {
            const timezone = project?.timezone;
            const todayStr = (timezone ? dayjs().tz(timezone) : dayjs()).format("YYYY-MM-DD");
            return date.toString() > todayStr;
          }}
          placeholder="Choose start & end date"
        >
          <DatePicker.Label>Date</DatePicker.Label>
          <DatePicker.Input placeholder="Choose start & end date" />
          <DatePicker.Wrapper className="z-10">
            <DatePicker.YearView />
            <DatePicker.MonthView />
            <DatePicker.DayView />
          </DatePicker.Wrapper>
        </DatePicker>
      </div>

      {startDate && endDate && (
        <div className="flex overflow-hidden flex-col flex-1 gap-4">
          {isError ? (
            <div className="flex flex-col flex-1 gap-3 justify-center items-center text-gray-400">
              <AlertCircle width={32} height={32} />
              <p className="text-body4">Failed to load work history.</p>
              <button
                type="button"
                className="px-4 py-2 text-blue-500 text-label2 rounded-8 bg-blue-10"
                onClick={() => refetch()}
              >
                Try again
              </button>
            </div>
          ) : isLoading ? (
            <DailyWorkListSkeleton isDesktopPanel={isDesktopPanel} />
          ) : (
            <>
              <div className="flex gap-2 items-center md:gap-3 md:items-stretch">
                <div className="flex flex-col flex-1 gap-1 justify-center items-center p-4 border border-blue-500 bg-blue-10 rounded-8 md:items-start md:justify-start md:gap-2 md:border-gray-200 md:bg-white">
                  <span className="flex flex-col gap-1 items-center md:flex-row md:items-center md:gap-1.5">
                    <HardHat width={16} height={16} className="md:text-gray-500" />
                    <span className="text-label2 md:text-label3 md:text-gray-600">Total Workforce</span>
                  </span>
                  <span className="text-gray-800 text-subtitle4 md:text-title3 md:text-gray-900">
                    {data?.summary.total_workforce ?? "-"}
                  </span>
                  {statSummary.totalDays > 0 && (
                    <span className="hidden md:block md:mt-auto md:pt-1 text-label3 text-gray-500">
                      Recorded {statSummary.loggedDays} days · Avg {statSummary.avgWorkforcePerLoggedDay}/day
                    </span>
                  )}
                </div>

                <div className="flex flex-col flex-1 gap-1 justify-center items-center p-4 border border-blue-500 bg-blue-10 rounded-8 md:items-start md:justify-start md:gap-2 md:border-gray-200 md:bg-white">
                  <span className="flex flex-col gap-1 items-center md:flex-row md:items-center md:gap-1.5">
                    <Pickaxe width={16} height={16} className="md:text-gray-500" />
                    <span className="text-label2 md:text-label3 md:text-gray-600">Total Equipment</span>
                  </span>
                  <span className="text-gray-800 text-subtitle4 md:text-title3 md:text-gray-900">
                    {data?.summary.total_equipment ?? "-"}
                  </span>
                  {statSummary.totalDays > 0 && (
                    <span className="hidden md:block md:mt-auto md:pt-1 text-label3 text-gray-500">
                      Recorded {statSummary.loggedDays} days · Avg {statSummary.avgEquipmentPerLoggedDay}/day
                    </span>
                  )}
                </div>

                {/* Log Submission: 참여 업체 없이 daily_stats만으로 계산 가능한 값만 보여 준다 */}
                {statSummary.totalDays > 0 && (
                  <div className="hidden flex-col flex-1 gap-2 items-start p-4 border border-gray-200 bg-white rounded-8 md:flex">
                    <span className="flex items-center gap-1.5">
                      <CalendarCheck width={16} height={16} className="text-gray-500" />
                      <span className="text-label3 text-gray-600">Log Submission</span>
                    </span>
                    <span className="text-gray-900 text-title3">
                      {statSummary.loggedDays}
                      <span className="ml-1 font-normal text-label3 text-gray-600">/ {statSummary.totalDays} days</span>
                    </span>
                    <span className="mt-auto pt-1 text-label3 text-gray-500">
                      {statSummary.missingDays === 0
                        ? "All days logged"
                        : `${statSummary.missingDays} day${statSummary.missingDays === 1 ? "" : "s"} missing`}
                    </span>
                  </div>
                )}
              </div>

              {isDesktopPanel ? (
                <div className="flex overflow-hidden flex-1 gap-5 items-start">
                  <div className="flex overflow-hidden flex-col flex-1 min-w-0 max-h-full rounded-8 border border-gray-200 bg-white">
                    <div className="flex gap-2 items-center px-4 py-3 border-b border-gray-200 shrink-0">
                      <h2 className="text-subtitle4 text-gray-1000">Daily Breakdown</h2>
                      <span className="text-label3 text-gray-500">· {statSummary.totalDays} days</span>
                      <span className="flex-1" />
                      <Button
                        type="button"
                        variant="outline"
                        color="secondary"
                        size="small"
                        prefixIcon={<SquareArrowOutUpRight width={14} height={14} />}
                        onClick={openExportDialog}
                      >
                        Export
                      </Button>
                    </div>

                    <div className="overflow-y-auto flex-1">
                      {dailyStats.length === 0 ? (
                        <EmptyRangeState />
                      ) : (
                        <table className="w-full table-fixed border-collapse text-left" aria-label="Daily breakdown">
                          <WorkHistoryTableHead />
                          <tbody>
                            {dailyStats.map((stat, idx) => (
                              <WorkHistoryTableRow
                                key={dailyStatKey(stat, idx)}
                                stat={stat}
                                projectId={projectId}
                                isSelected={stat.date === selectedDate}
                                onSelect={() => handleSelectDate(stat.date)}
                              />
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-gray-300 bg-white">
                              <td colSpan={2} className="px-4 py-3 text-subtitle4 text-gray-1000">
                                Total
                              </td>
                              <td className="px-4 py-3 text-subtitle4 text-gray-1000">
                                {data?.summary.total_workforce ?? 0}
                              </td>
                              <td className="px-4 py-3 text-subtitle4 text-gray-1000">
                                {data?.summary.total_equipment ?? 0}
                              </td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      )}
                    </div>
                  </div>

                  <div className="w-[360px] shrink-0 sticky top-0 self-start rounded-16 border border-gray-200 bg-white p-4 max-h-full overflow-y-auto">
                    {selectedDate ? (
                      <WorkHistoryDayContent
                        projectId={projectId}
                        date={selectedDate}
                        activeTab={activeTab}
                        onActiveTabChange={setActiveTab}
                        dailyLogId={selectedDailyLogId}
                      />
                    ) : (
                      <EmptyPanelState />
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex overflow-hidden flex-col flex-1 gap-2">
                  <WorkHistoryBreakdownHeader onExport={openExportDialog} />

                  {dailyStats.length === 0 ? (
                    <EmptyRangeState />
                  ) : (
                    <ol
                      className="flex overflow-auto flex-col flex-1 gap-2 pb-4"
                      style={{ paddingBottom: 16 + bottom }}
                    >
                      {dailyStats.map((stat, idx) => (
                        <WorkHistoryListItem
                          key={dailyStatKey(stat, idx)}
                          stat={stat}
                          projectId={projectId}
                          onSelect={() => handleSelectDate(stat.date)}
                        />
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!isDesktopPanel && (
        <WorkHistoryBottomSheet
          isOpen={!!selectedDate}
          onClose={() => setUserSelectedDate(null)}
          date={selectedDate ?? ""}
          projectId={projectId}
          activeTab={activeTab}
          onActiveTabChange={setActiveTab}
          dailyLogId={selectedDailyLogId}
        />
      )}
    </div>
  );
};

export default DailyWorkList;
